import { GetStaticPropsContext, InferGetStaticPropsType } from 'next';
import { ReactElement } from 'react';
import { Storyblok, useStoryblok } from 'storyblok';

import { Layout } from '@/components';
import Card from '@/components/global/Card';
import Footer from '@/components/global/Footer';
import Header from '@/components/global/Header';
import BaseSeo from '@/components/seo/BaseSeo';
import { ChildPageList, LargeCardGrid } from '@/components/StoryblokPages';
import { VERSION } from '@/lib/constants';
import { getStoryBlokProps } from '@/lib/storyblok/storyblokGetStaticProps';
import { getStoryBlokNavLinks } from '@/lib/storyblok/storyblokNavLinks';
import { getStoryblokPages } from '@/lib/storyblok/storyblokSiblingPageNav';

const sbPageParams = (preview) => {
  return {
    preview,
    starts_with: 'articles-guides/',
    getTags: true,
    startPage: false
  };
};

const Page = ({
  page,
  story,
  preview,
  resolve_relations,
  childPageData
}: InferGetStaticPropsType<typeof getStaticProps>) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const sbStory = preview ? useStoryblok(story, preview, resolve_relations) : story;
  const globalLinks = getStoryBlokNavLinks(story);

  const {
    content: { headerTheme = '', footerTheme = '', SEO = {} },
    name = '',
    full_slug
  } = sbStory;

  const haveChildPages = childPageData.pages && childPageData.pages.length > 0;

  return (
    <div>
      <BaseSeo
        title={SEO?.title}
        description={SEO?.description}
        ogDescription={SEO?.og_description}
        ogTitle={SEO?.og_title}
        image={SEO?.og_image}
        path={full_slug}
      />
      <Header
        blok={sbStory.content}
        navigation={globalLinks}
        headerTheme={headerTheme}
        pageName={name}
        hasBorder
      />
      <main id="main" className="page-padding-top">
        {haveChildPages && (
          <>
            <LargeCardGrid
              sbPageParams={sbPageParams(preview)}
              childPageData={childPageData}
              parentSlug="articles_guides"
              cardVariant="articles-card"
            />
            {/* Hidden for now. TODO: Uncomment if needed in the future releases
            <ChildPageList
              childPages={childPageData.pages.slice(9, 100)}
              parentSlug="articles_guides"
            /> */}
          </>
        )}
      </main>
      <Footer theme={footerTheme} navigation={globalLinks.footerLinks} />
    </div>
  );
};

export default Page;

export interface pathParams {
  channel: string;
  locale: string;
  slug: string;
}

export const getStaticProps = async (context: GetStaticPropsContext) => {
  const pageSlug = 'articles-guides';
  const preview = context.preview || false;
  const resolveRelations = 'page.globalHeader,page.globalFooter';
  const story = await getStoryBlokProps({ pageSlug, context, VERSION, resolveRelations });

  if (story?.notFound) {
    return story;
  }

  const childPageData = await getStoryblokPages(sbPageParams(preview));

  return {
    props: {
      preview,
      story: story || null,
      resolve_relations: resolveRelations,
      childPageData
    },
    revalidate: 60 * 60 // value in seconds, how often ISR will trigger on the server
  };
};

Page.getLayout = function getLayout(page: ReactElement) {
  return <Layout>{page}</Layout>;
};
